import * as z from "zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { TrialBanner } from "@/components/shared/TrialBanner";

export function RegisterForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Zod schema with translations
  const formSchema = z.object({
    name: z.string()
      .max(50, t("validation.nameTooLong"))
      .optional()
      .refine((val) => !val || val.length >= 2 || val === "", {
        message: t("validation.nameTooShort"),
      }),
    email: z.string()
      .email(t("validation.invalidEmail"))
      .min(1, t("validation.required")),
    password: z.string()
      .min(8, t("validation.passwordTooShort"))
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, t("validation.passwordTooWeak")),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t("validation.passwordMismatch"),
    path: ["confirmPassword"],
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Password strength checker
  const checkPasswordRequirement = (password: string, type: string) => {
    switch (type) {
      case "length":
        return password.length >= 8;
      case "uppercase":
        return /[A-Z]/.test(password);
      case "lowercase":
        return /[a-z]/.test(password);
      case "number":
        return /\d/.test(password);
      default:
        return false;
    }
  };

  const password = form.watch("password");

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    
    try {
      // Step 1: Create Firebase Auth user
      // This automatically triggers the onUserCreated Cloud Function
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );

      console.log("✓ Firebase Auth user created:", userCredential.user.uid);

      // Step 2: Send verification email
      try {
        await sendEmailVerification(userCredential.user, {
          handleCodeInApp: false,
          url: window.location.origin + "/email-sent",
        });
        console.log("✓ Verification email sent");
      } catch (verifyError: any) {
        console.error("❌ Error sending verification email:", verifyError);
        toast({
          title: t("register.verificationEmailFailed"),
          description: t(`errors.${verifyError.code}`, { defaultValue: t("errors.default") }),
          variant: "destructive",
        });
        // Continue navigating so user can use the resend button on the next page
      }

      // Step 3: Cloud Function automatically creates Firestore documents
      // No client-side Firestore writes needed!

      toast({
        title: t("register.success"),
        description: t("register.checkEmail"),
      });

      // Wait for Cloud Function to complete (give it 2 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Redirect to email sent page
      navigate(`/email-sent?email=${encodeURIComponent(userCredential.user.email || "")}`);
    } catch (error: any) {
      console.error("Registration error:", error);
      
      // Handle specific error codes
      let errorMessage = t("errors.default");
      
      if (error.code === "auth/email-already-in-use") {
        errorMessage = t("errors.auth/email-already-in-use");
      } else if (error.code === "auth/invalid-email") {
        errorMessage = t("errors.auth/invalid-email");
      } else if (error.code === "auth/weak-password") {
        errorMessage = t("errors.auth/weak-password");
      }

      toast({
        title: t("errors.default"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Card className="w-full shadow-lg border-primary/10">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center text-primary">
          {t("register.title")}
        </CardTitle>
        <CardDescription className="text-center">
          {t("register.subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <TrialBanner />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Name Field - OPTIONAL */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("register.name")} {t("common.optional")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={t("register.namePlaceholder")}
                      autoComplete="name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("register.email")}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t("register.emailPlaceholder")}
                      autoComplete="email"
                      dir="ltr"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password Field */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("register.password")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder={t("register.passwordPlaceholder")}
                        autoComplete="new-password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute end-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />

                  {/* Password Requirements */}
                  {password && (
                    <div className="mt-3 space-y-2 rounded-lg bg-muted p-3">
                      <p className="text-sm font-medium text-muted-foreground">
                        {t("register.passwordRequirements.title")}
                      </p>
                      <div className="space-y-1">
                        {[
                          { key: "minLength", type: "length" },
                          { key: "uppercase", type: "uppercase" },
                          { key: "lowercase", type: "lowercase" },
                          { key: "number", type: "number" },
                        ].map(({ key, type }) => (
                          <div key={key} className="flex items-center gap-2 text-sm">
                            {checkPasswordRequirement(password, type) ? (
                              <CheckCircleIcon className="h-4 w-4 text-success" />
                            ) : (
                              <XCircleIcon className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span
                              className={cn(
                                checkPasswordRequirement(password, type)
                                  ? "text-success font-medium"
                                  : "text-muted-foreground"
                              )}
                            >
                              {t(`register.passwordRequirements.${key}`)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </FormItem>
              )}
            />

            {/* Confirm Password Field */}
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("register.confirmPassword")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder={t("register.confirmPasswordPlaceholder")}
                        autoComplete="new-password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute end-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? t("common.loading") : t("register.createAccount")}
            </Button>

            {/* Terms */}
            <p className="text-center text-xs text-muted-foreground">
              {t("register.termsAgree")}{" "}
              <a href="/terms" className="text-primary hover:underline">
                {t("register.termsLink")}
              </a>{" "}
              {t("register.and")}{" "}
              <a href="/privacy" className="text-primary hover:underline">
                {t("register.privacyLink")}
              </a>
            </p>
          </form>
        </Form>

        {/* Sign In Link */}
        <div className="mt-6 text-center text-sm">
          <span className="text-muted-foreground">{t("register.alreadyHaveAccount")}</span>{" "}
          <a href="/login" className="font-semibold text-primary hover:underline">
            {t("register.signIn")}
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
